import { apiGet } from "../client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@/lib/query-keys";
import useLoginStore from "@/stores/loginStore";
import { useEffect } from "react";

// 그룹 정보 인터페이스
export interface IUserGroup {
  id: string;
  name: string;
  status: string;
  user_count: number;
  created_at: string;
  updated_at: string;
}

// 사용자 정보 인터페이스 정의
export interface IUser {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  created_at: string;
  updated_at: string;
  profileImageUrl?: string;

  // 학생 사용자 정보
  grade?: number;
  is_graduated?: boolean;
  school?: string;
  phone?: string;
  parent_phone?: string;
  memo?: string;
  allow_early_submission?: boolean; // 조기 제출 허용 여부 (해외 학생용)

  // 그룹 정보
  group_id?: string;
  group?: IUserGroup;
}

/**
 * 현재 로그인한 사용자의 정보를 가져옵니다.
 * 이 API는 인증이 필요하며, 토큰이 필요합니다.
 */
export const apiGetMe = async () => {
  // /users/me 엔드포인트로 요청
  const response = await apiGet<IUser>("/users/me");

  return response;
};

/**
 * 현재 로그인한 사용자의 정보를 React Query를 통해 가져오는 훅
 * 로그인 상태에 따라 쿼리를 자동으로 활성화/비활성화하고,
 * 로그인/로그아웃 시 즉시 쿼리를 새로고침합니다.
 * @param options - React Query 옵션 (staleTime, cacheTime 등)
 * @returns React Query에서 제공하는 모든 속성 및 메서드
 */
export const useQueryGetMe = (options = {}) => {
  const { isAuthenticated } = useLoginStore();
  const queryClient = useQueryClient();

  // 로그인 상태가 변경될 때마다 쿼리 무효화
  useEffect(() => {
    if (isAuthenticated) {
      // 로그인 시: 쿼리 무효화하여 최신 사용자 정보 가져오기
      queryClient.invalidateQueries({ queryKey: QueryKeys.user.me() });
    } else {
      // 로그아웃 시: 쿼리 데이터 제거
      queryClient.removeQueries({ queryKey: QueryKeys.user.me() });
    }
  }, [isAuthenticated, queryClient]);

  return useQuery({
    queryKey: QueryKeys.user.me(),
    queryFn: async () => {
      const response = await apiGetMe();
      return response.data;
    },
    enabled: isAuthenticated, // 로그인된 상태에서만 쿼리 실행
    staleTime: 5 * 60 * 1000, // 5분
    ...options,
  });
};

export default useQueryGetMe;
